import { useParams, useOutletContext, Link, Navigate } from "react-router-dom";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowUpRight, CalendarBlank, Clock } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { buildPath, canonicalSlug } from "@/lib/routes";
import { toLangSlug } from "@/lib/slug_map";
import { useLang } from "@/hooks/useLang";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/motion/Reveal";
import LeadMagnet from "@/components/LeadMagnet";
import { articleSchema, breadcrumbSchema } from "@/lib/schema";
import { SERVICE_ITEMS } from "@/lib/nav";

export default function BlogPost() {
  const { slug: urlSlug } = useParams();
  const { lang, t } = useLang();
  const { profile } = useOutletContext();
  const daSlug = canonicalSlug("post", urlSlug);
  const { data: post, isLoading } = useQuery({ queryKey: ["post", daSlug], queryFn: () => api.getBlogPost(daSlug) });
  const c = post ? (post[lang] || post.da) : null;

  const { toc, html } = useMemo(() => {
    if (!c || typeof document === "undefined") return { toc: [], html: c?.body || "" };
    const div = document.createElement("div");
    div.innerHTML = c.body || "";
    const heads = Array.from(div.querySelectorAll("h2"));
    const toc = heads.map((h, i) => {
      const id = `section-${i}`;
      h.setAttribute("id", id);
      return { id, text: h.textContent };
    });
    return { toc, html: div.innerHTML };
  }, [c]);

  const canonicalUrlSlug = toLangSlug("post", daSlug, lang);
  if (canonicalUrlSlug && urlSlug !== canonicalUrlSlug) {
    return <Navigate to={buildPath("post", lang, { slug: daSlug })} replace />;
  }

  if (isLoading || !post) return <div className="px-6 md:px-20 py-32 text-muted-foreground">…</div>;
  const path = buildPath("post", lang, { slug: daSlug });
  const dateStr = new Date(post.date).toLocaleDateString(lang === "da" ? "da-DK" : "en-US", { year: "numeric", month: "long", day: "numeric" });
  const relatedServices = SERVICE_ITEMS.filter((s) => (post.services || []).includes(s.slug));

  return (
    <>
      <Seo routeKey="post" params={{ slug: daSlug }} lang={lang} title={c.meta_title} description={c.meta_description} image={post.cover} type="article"
        jsonLd={[
          articleSchema(post, lang, path, profile?.name),
          breadcrumbSchema([{ name: t("nav_home"), path: buildPath("home", lang) }, { name: t("nav_blog"), path: buildPath("blog", lang) }, { name: c.title, path }]),
        ]} />

      <article>
        <header className="border-b border-border px-6 md:px-12 lg:px-20 pt-12 pb-12">
          <Link to={buildPath("blog", lang)} className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary">
            <ArrowLeft size={16} /> {t("back_to_blog")}
          </Link>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="rounded-full bg-secondary px-3 py-1 text-primary">{post.category}</span>
            <span className="flex items-center gap-1.5 font-mono"><CalendarBlank size={14} /> {dateStr}</span>
            <span className="flex items-center gap-1.5 font-mono"><Clock size={14} /> {post.read_time} {t("min_read")}</span>
          </div>
          <h1 className="mt-6 max-w-4xl font-heading text-4xl md:text-6xl font-black tracking-tighter leading-[0.95]">{c.title}</h1>
        </header>

        <div className="px-6 md:px-12 lg:px-20 py-12">
          <div className="grid gap-14 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <Reveal>
                <div className="mb-10 grayscale-hover overflow-hidden rounded-2xl border border-border">
                  <img src={post.cover} alt={c.title} className="aspect-[16/9] w-full object-cover" />
                </div>
              </Reveal>
              <div className="prose-mls max-w-none" dangerouslySetInnerHTML={{ __html: html }} />
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-24">
                {toc.length > 0 && (
                  <nav className="rounded-xl border border-border bg-card p-6" data-testid="article-toc" aria-label="Table of contents">
                    <div className="label-mono text-muted-foreground/60">{lang === "da" ? "Indhold" : "On this page"}</div>
                    <ul className="mt-4 space-y-2.5 border-l border-border">
                      {toc.map((item) => (
                        <li key={item.id}>
                          <a href={`#${item.id}`}
                            className="-ml-px block border-l-2 border-transparent pl-4 text-sm text-muted-foreground transition-colors hover:border-foreground hover:text-foreground">
                            {item.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                )}
              </div>
            </aside>
          </div>
        </div>

        {relatedServices.length > 0 && (
          <section className="border-t border-border px-6 md:px-12 lg:px-20 py-16">
            <div className="label-mono mb-8 text-muted-foreground/60">{t("related_services")}</div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {relatedServices.map((s) => (
                <Link key={s.slug} to={buildPath("service", lang, { slug: s.slug })} data-testid={`post-service-${s.slug}`}
                  className="card-hover group flex items-center justify-between rounded-xl border border-border bg-card p-6">
                  <span className="font-heading text-lg font-bold">{s[lang]}</span>
                  <ArrowUpRight size={18} className="text-muted-foreground group-hover:text-foreground" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Read also */}
        {post.related_posts?.length > 0 && (
          <section className="border-t border-border px-6 md:px-12 lg:px-20 py-16">
            <div className="label-mono mb-8 text-primary">{t("read_also")}</div>
            <div className="grid gap-6 md:grid-cols-2">
              {post.related_posts.map((rp) => {
                const rc = rp[lang] || rp.da;
                return (
                  <Link key={rp.slug} to={buildPath("post", lang, { slug: rp.slug })} data-testid={`related-post-${rp.slug}`}
                    className="group flex items-center gap-5 overflow-hidden rounded-xl border border-border bg-card card-hover">
                    <div className="grayscale-hover h-28 w-32 shrink-0 overflow-hidden">
                      <img src={rp.cover} alt={rc.title} className="h-full w-full object-cover" />
                    </div>
                    <div className="py-4 pr-4">
                      <span className="font-mono text-xs text-primary">{rp.category}</span>
                      <h3 className="mt-1 font-heading text-lg font-bold leading-snug">{rc.title}</h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </article>
      <LeadMagnet profile={profile} />
    </>
  );
}
